import React, { createContext, useContext, useEffect, useState } from 'react';
import API from '../../Services/API';
import { useToasts } from '../Toasts';
import { isLocal, useVisibilityChange } from '../../Services/helpers';

const captureException = import(/* webpackPreload: true, webpackChunkName: "ui-components" */'../../Services/Sentry').then(module => ({ default: module.captureException }));

const SessionContext = createContext();

export const useSessionContext = () => {
    return useContext(SessionContext);
};

export const Provider = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const [hasToken, setHasToken] = useState(!!API.getToken());
    const [user, setUser] = useState(null);
    const [business, setBusiness] = useState(null);
    const [plan, setPlan] = useState(null);
    const [balance, setBalance] = useState(null);
    const [journal, setJournal] = useState(null);
    const [notifications, setNotifications] = useState(null);
    const [notificationBadge, setNotificationBadge] = useState(null);
    const {showError} = useToasts();
    const [deviceToken, setDeviceToken] = useState(null);
    const [notificationsFetchError, setNotificationsFetchError] = useState(null);
    const [sessionId, setSessionId] = useState(null);

    const fetchDetails = async () => {
        try {
            const details = await API.get('/app/start');
            setUser(details.user);
            setBusiness(details.business);
            setPlan(details.plan);
            setBalance(details.balance);
            setJournal(details.journal);
            setIsReady(true);
        } catch (e) {
            console.log(e);
            showError("Failed to fetch session details", e.message);

        }
    }

    const fetchNotifications = async () => {
        if (!API.getToken()) {
            return;
        }

        try {
            const _notifications = await API.get('/notifications/recent');
            setNotificationsFetchError(false);
            setNotifications(_notifications);
            setNotificationBadge(_notifications.filter(n => n.status === 'Active').length);
        } catch (e) {
            // console.log(captureException)
            captureException.then(e);
            setNotificationsFetchError(true);
        }
    }

    const markAllNotificationsSeen = () => {
        return API.post('/notifications/markAllSeen')
            .catch(err => showError(API.parseErrorResponse(err)))
            .then(fetchNotifications);
    }

    const markMatchingNotificationsSeen = (filter) => {
        if (!notifications || !notifications.length) {
            return;
        }

        const matches = notifications.filter(n => n.status === 'Active').filter(filter);

        if (!matches || !matches.length) {
            return;
        }

        const promises = matches.map(n => API.post(`/notifications/${n.notificationId}/see`));

        Promise.all(promises)
            .catch(err => showError(API.parseErrorResponse(err)))
            .then(fetchNotifications);
    }

    const hasFeature = (feature) => {
        if (!plan) {
            return false;
        }

        const disabled = [
            // 'ADDITIONAL_USERS',
            // 'WEEK_PLANNER',
            // 'BANK_RECONCILIATION',
            // 'AI_ASSISTANT',
            // 'SAVED_REPLIES',
            // 'ROUTE_OPTIMISATION',
            // 'CUSTOMER_PORTAL',
            // 'ADVANCED_SCHEDULING',
            // 'EXPENSES',
            // 'LOYALTY_CARDS',
            // 'DOCUMENTS',
            // 'TAGS'
            // 'SAVED_SEARCHES',
        ];
        if (isLocal() && disabled.includes(feature)) {
            console.log('Feature disabled in dev mode', feature);
            return false;
        }

        return plan?.features[feature]?.enabled ?? false;
    }

    const isRoleSufficient = (role) => {
        return API.isRoleSufficient(role);
    }

    const login = (data) => {
        return API.post('/auth/login', data)
            .then((res) => {
                API.setToken(res.token);
                setUser(res.user);
                setBusiness(res.business);
            })
            .catch(err => {
                showError(API.parseErrorResponse(err));
            })
    }

    const logout = () => {
        return API.post('/auth/logout', {deviceToken})
            .then(() => {
                setIsReady(false);
                setUser(null);
                setBusiness(null);
                setBalance(null);
                setPlan(null);
                API.reset();
            })
            .catch((err) => {
                showError(API.parseErrorResponse(err));
            });
    }

    useEffect(() => {
        setHasToken(!!API.getToken());

        if (API.getToken()) {
            const sid = API.jwt('sid');
            if (sid !== sessionId) {
                setSessionId(sid);
            }

            fetchDetails();
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [API.getToken()]);

    useVisibilityChange(() => {
        fetchNotifications();
    });

    const providerValues = {
        user,
        business,
        plan,
        balance,
        journal,
        notifications,
        notificationBadge,
        setNotificationBadge,
        notificationsFetchError,
        markAllNotificationsSeen,
        markMatchingNotificationsSeen,
        fetchDetails,
        fetchNotifications,
        hasFeature,
        refreshSession: fetchDetails,
        refresh: fetchDetails,
        login,
        logout,
        hasToken,
        isReady,
        isRoleSufficient,
        isAdmin: (level) => API.isAdmin(level),
        isAdminSwitched: API.isAdminSwitchedBusiness(),
        adminSwitchBack: () => API.switchBack(),
        setDeviceToken,
        sessionId,
    };

    return (
        <SessionContext.Provider value={providerValues}>
            {children}
        </SessionContext.Provider>
    );
};
